'use client';
import type { ReactNode } from 'react';
import React from 'react';
import { useStore } from 'zustand';
import type { ChartId } from '@/types/commontypes';
import { ShapeType } from '@/types/commontypes';
import { globalShapeStore } from '../stores/globalShapeStore';
import type { GlobalShapeStore } from '../stores/globalShapeStore';
import { getShortSymbolName } from '../utils/utils';

export interface ToolbarState {
  x: number;
  y: number;
  width: number;
  height: number;
}

/**
 * useGlobalShapes — 기존 Context 인터페이스를 zustand store로 위임.
 *
 * - 모든 state는 `globalShapeStore`에서 관리.
 * - `getState()`로 항상 최신 값을 읽을 수 있어 stale closure 문제 없음.
 * - `shapeJustSelected`는 동기적 플래그로 store state에 포함.
 * - `lastShapeProperties`는 store에서 관리하며 localStorage와 자동 동기화.
 */
export function useGlobalShapes() {
  return useStore(globalShapeStore);
}

/**
 * selector 기반 구독 — 필요한 필드만 구독하여 불필요한 리렌더링 방지.
 *
 * @example
 * const shapeTypeToDraw = useGlobalShapeSelector(s => s.shapeTypeToDraw);
 */
export function useGlobalShapeSelector<T>(selector: (state: GlobalShapeStore) => T): T {
  return useStore(globalShapeStore, selector);
}

/**
 * GlobalShapesProvider — 하위 호환을 위해 유지하지만 내부적으로 아무것도 하지 않음.
 * globalShapeStore는 모듈 레벨 싱글턴이므로 Provider 없이도 동작한다.
 */
export const GlobalShapesProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  return <>{children}</>;
};

// ── 유틸 (기존 export 유지) ──

export { saveLastShapeProperties } from '../stores/globalShapeStore';

// ── 충돌 감지 ──

export interface ConflictResult {
  canEnterLong: boolean;
  canEnterShort: boolean;
  canEnterHedge: boolean;
}

/**
 * on-demand 충돌 감지 함수.
 * globalShapeStore의 activeTimeframes를 읽어 localStorage를 스캔한 뒤
 * 진입 가능 여부를 반환한다. + 버튼 / 헷지진입 클릭 직전에 호출한다.
 *
 * hedgeMode OFF: 어떤 오더라인이든 존재하면 모든 진입 차단 (AC-46)
 * hedgeMode ON:
 *   - 롱만 존재 → 롱·헷지 차단, 숏만 허용 (AC-47)
 *   - 숏만 존재 → 숏·헷지 차단, 롱만 허용 (AC-48)
 *   - 롱+숏 모두 존재 → 모든 진입 차단 (AC-49)
 *   - 없음 → 롱·숏·헷지 모두 허용
 */
export function scanOrderLineConflict(symbol: string, hedgeMode: boolean): ConflictResult {
  const { activeTimeframes } = globalShapeStore.getState();
  const shortSymbol = getShortSymbolName(symbol);

  let hasLong = false;
  let hasShort = false;

  for (const tf of activeTimeframes) {
    const key = `shapes_${shortSymbol}_${tf}`;
    try {
      const raw = localStorage.getItem(key);
      if (!raw) continue;
      // 저장 포맷: { [chartId]: { ORDER_LINE: StorageShape[] } }
      // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
      const parsed = JSON.parse(raw) as Record<string, Record<string, unknown[]>>;
      for (const chartData of Object.values(parsed)) {
        const orderLines = chartData[ShapeType.ORDER_LINE];
        if (!Array.isArray(orderLines)) continue;
        for (const shape of orderLines) {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
          const s = shape as Record<string, unknown>;
          if (s['positionType'] === 'long') hasLong = true;
          if (s['positionType'] === 'short') hasShort = true;
          if (hasLong && hasShort) break;
        }
        if (hasLong && hasShort) break;
      }
    } catch {
      // localStorage 파싱 실패 시 무시
    }
    if (hasLong && hasShort) break;
  }

  if (!hedgeMode) {
    const blocked = hasLong || hasShort;
    return { canEnterLong: !blocked, canEnterShort: !blocked, canEnterHedge: false };
  }

  // hedgeMode ON
  if (hasLong && hasShort) {
    return { canEnterLong: false, canEnterShort: false, canEnterHedge: false };
  }
  if (hasLong) {
    return { canEnterLong: false, canEnterShort: true, canEnterHedge: false };
  }
  if (hasShort) {
    return { canEnterLong: true, canEnterShort: false, canEnterHedge: false };
  }
  return { canEnterLong: true, canEnterShort: true, canEnterHedge: true };
}

export const getMeasureKey = (chartId: ChartId, timeframe?: string) => `${chartId}_${timeframe ?? 'default'}`;

/**
 * 이벤트 핸들러, setTimeout 등 React 렌더 사이클 바깥에서 최신 상태를 읽을 때 사용.
 *
 * @example
 * setTimeout(() => {
 *   const { selectedShape } = getGlobalShapeState();
 * }, 0);
 */
export const getGlobalShapeState = () => globalShapeStore.getState();
